import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { Action, Resource } from '~/api/graphql';
import { usePermissions } from '~/components/permissions';
import { useSettingsUsageTabEnabled } from '~/components/use-settings-usage-tab-enabled';
import { type CopilotActionError, normalizeCopilotActionError } from '~/lib/copilot-action-error';

export const OVERAGE_LIMITS_PATH = '/app/settings/limits';
export const OVERAGE_USAGE_PATH = '/app/settings/usage';

const linkClassName =
  'font-medium text-red-800 dark:text-red-300 underline underline-offset-2 hover:text-red-900 dark:hover:text-red-200';

/**
 * Inline banner shown when create/send is blocked by an overage spend limit.
 * Uses the server message; appends a Limits or Usage deep-link when available
 * (same gates as Settings → Limits / Usage).
 */
export const OverageLimitErrorBanner = (props: { message: string; className?: string }) => {
  const { hasPermission } = usePermissions();
  const usageTabEnabled = useSettingsUsageTabEnabled();
  const canUpdateLimits = usageTabEnabled && hasPermission(Resource.Subscription, Action.Update);

  const cta = canUpdateLimits ? (
    <Link to={OVERAGE_LIMITS_PATH} className={linkClassName}>
      View or change limits
    </Link>
  ) : null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={clsx(
        'rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-3 text-left',
        props.className
      )}
    >
      <p className="m-0 text-sm font-semibold text-red-800 dark:text-red-300">
        Spend limit reached
      </p>
      <p className="mt-1 mb-0 text-sm text-red-700 dark:text-red-300">
        {props.message}
        {cta && <> {cta}</>}
      </p>
    </div>
  );
};

/**
 * Renders an overage banner or plain red text for other submit errors.
 * Uses a plain element (not Fieldset ErrorMessage) so it works outside Field.
 */
export const CopilotSubmitError = (props: {
  error?: CopilotActionError | string | null;
  className?: string;
}) => {
  const normalized = normalizeCopilotActionError(props.error);
  if (!normalized) return null;
  if (normalized.kind === 'overage') {
    return <OverageLimitErrorBanner message={normalized.message} className={props.className} />;
  }
  return (
    <p
      role="alert"
      className={clsx('m-0 text-sm/5 text-red-600 sm:text-xs/4 dark:text-red-500', props.className)}
    >
      {normalized.message}
    </p>
  );
};
